import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { saveAs } from 'file-saver';
import { extractKeywords } from '../services/openaiService';
import { parseResumeFile, testResumeParsingWithText } from '../services/resumeParserService';
import templateService from '../services/templateService';
import { generateResumePdf } from '../services/resumePdfService';
import { useUser } from '../contexts/UserContext';
import './ResumeBuilderStudio.theme.css';

const TEMPLATE_OPTIONS = [
  { id: 1, name: 'Professional', accent: '#e0a3ff' },
  { id: 2, name: 'Modern', accent: '#00ffff' },
  { id: 3, name: 'Simple', accent: '#ff69b4' },
  { id: 4, name: 'Academic', accent: '#9370db' },
];

const SECTIONS = [
  { id: 'personal', title: 'Personal Info', icon: '👤' },
  { id: 'summary', title: 'Professional Summary', icon: '📝' },
  { id: 'experience', title: 'Work Experience', icon: '💼' },
  { id: 'education', title: 'Education', icon: '🎓' },
  { id: 'skills', title: 'Skills', icon: '⚡' },
  { id: 'projects', title: 'Projects', icon: '🚀' },
  { id: 'certifications', title: 'Certifications', icon: '🏆' },
];

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="rbs-field">
    <span>{label}</span>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </label>
);

const TextArea = ({ label, value, onChange, placeholder, rows = 4 }) => (
  <label className="rbs-field">
    <span>{label}</span>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  </label>
);

const ResumeBuilderStudio = ({ setShowAuthModal }) => {
  const fileRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  /* ----------------- state ----------------- */
  const [jobDescription, setJobDescription] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const [openSection, setOpenSection] = useState('personal');
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadErr, setDownloadErr] = useState('');
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);

  // Resume preview zoom + pan.
  // The paper renders at BASE_SCALE when the control reads 100% — i.e. the old
  // "80%" view is the new default/100%. Displayed % is always `zoom * 100`,
  // while the actual transform uses `zoom * BASE_SCALE`.
  const BASE_SCALE = 0.8;
  const ZOOM_MIN = 0.6;
  const ZOOM_MAX = 2.4;
  const ZOOM_STEP = 0.2;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const panRef = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const effectiveScale = zoom * BASE_SCALE;
  // Keep the paper edges pinned to the viewport — never pan past them.
  const clampPan = (x, y, z = zoom) => {
    const vp = viewportRef.current;
    const stage = stageRef.current;
    const s = z * BASE_SCALE;
    if (!vp || !stage || s <= BASE_SCALE) return { x: 0, y: 0 };
    const maxX = Math.max(0, (stage.offsetWidth * s - vp.clientWidth) / 2); // origin: top center
    const overflowY = Math.max(0, stage.offsetHeight * s - vp.clientHeight); // origin: top
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(0, Math.max(-overflowY, y)),
    };
  };
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  // Re-clamp (and recenter) whenever the zoom level changes.
  useEffect(() => { setPan((p) => clampPan(p.x, p.y, zoom)); }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keyboard zoom: + / = to zoom in, - to zoom out, 0 to reset (incl. numpad).
  // Ignored while typing in a form field.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') { e.preventDefault(); zoomIn(); }
      else if (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract') { e.preventDefault(); zoomOut(); }
      else if (e.key === '0' || e.code === 'Numpad0') { zoomReset(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onPanStart = (e) => {
    if (effectiveScale <= BASE_SCALE) return; // only drag once the paper overflows the frame
    panRef.current = { active: true, startX: e.clientX, startY: e.clientY, origX: pan.x, origY: pan.y };
    setPanning(true);
  };
  const onPanMove = (e) => {
    if (!panRef.current.active) return;
    const nx = panRef.current.origX + (e.clientX - panRef.current.startX);
    const ny = panRef.current.origY + (e.clientY - panRef.current.startY);
    setPan(clampPan(nx, ny));
  };
  const onPanEnd = () => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    setPanning(false);
  };
  const [profileData, setProfileData] = useState(null);
  const [usingProfile, setUsingProfile] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    fullName: '', email: '', phone: '', location: '', linkedIn: '', portfolio: '',
  });
  const [summary, setSummary] = useState('');
  const [experiences, setExperiences] = useState([
    { id: 1, company: '', position: '', duration: '', description: '' },
  ]);
  const [education, setEducation] = useState([
    { id: 1, institution: '', degree: '', field: '', year: '' },
  ]);
  const [skills, setSkills] = useState('');
  const [projects, setProjects] = useState([
    { id: 1, name: '', technologies: '', description: '' },
  ]);
  const [certifications, setCertifications] = useState('');

  /* ----------------- profile fetch ----------------- */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.profile) setProfileData(data.profile);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, API_BASE_URL]);

  /* ----------------- credit ops ----------------- */
  const requireAuthAndCredits = useCallback(() => {
    if (!user) {
      if (typeof setShowAuthModal === 'function') setShowAuthModal(true);
      else setParseError('Please sign in to continue.');
      return false;
    }
    if (credits < 1) {
      setShowInsufficientCredits(true);
      return false;
    }
    return true;
  }, [user, credits, setShowAuthModal]);

  const deductCredit = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/resume/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          setShowInsufficientCredits(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }
      if (setCredits) setCredits(data.remainingCredits);
      return true;
    } catch (e) {
      setParseError(`Credit error: ${e.message}`);
      return false;
    }
  }, [API_BASE_URL, setCredits]);

  /* ----------------- handlers ----------------- */
  const handlePersonal = (field) => (val) => setPersonalInfo((p) => ({ ...p, [field]: val }));

  const updateList = (setter) => (id, field, val) =>
    setter((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: val } : it)));
  const addItem = (setter, defaults) => () =>
    setter((prev) => [...prev, { id: Date.now(), ...defaults }]);
  const removeItem = (setter) => (id) =>
    setter((prev) => (prev.length > 1 ? prev.filter((it) => it.id !== id) : prev));

  const updateExp = updateList(setExperiences);
  const addExp = addItem(setExperiences, { company: '', position: '', duration: '', description: '' });
  const removeExp = removeItem(setExperiences);

  const updateEdu = updateList(setEducation);
  const addEdu = addItem(setEducation, { institution: '', degree: '', field: '', year: '' });
  const removeEdu = removeItem(setEducation);

  const updateProj = updateList(setProjects);
  const addProj = addItem(setProjects, { name: '', technologies: '', description: '' });
  const removeProj = removeItem(setProjects);

  const handleExtractKeywords = useCallback(async () => {
    if (!jobDescription.trim()) {
      setParseError('Paste a job description first.');
      return;
    }
    if (!user) {
      if (typeof setShowAuthModal === 'function') setShowAuthModal(true);
      else setParseError('Please sign in to continue.');
      return;
    }
    setParseError('');
    setExtracting(true);
    try {
      const kws = await extractKeywords(jobDescription);
      setKeywords(Array.isArray(kws) ? kws : []);
    } catch (e) {
      setParseError(`Could not extract keywords: ${e.message}`);
    } finally {
      setExtracting(false);
    }
  }, [jobDescription, user, setShowAuthModal]);

  const fillFromParsed = useCallback((d) => {
    if (d.personalInfo) {
      setPersonalInfo({
        fullName: d.personalInfo.name || d.personalInfo.fullName || '',
        email: d.personalInfo.email || '',
        phone: d.personalInfo.phone || '',
        location: d.personalInfo.location || d.personalInfo.address || '',
        linkedIn: d.personalInfo.linkedin || '',
        portfolio: d.personalInfo.portfolio || d.personalInfo.website || '',
      });
    }
    if (d.summary || d.professionalSummary) setSummary(d.summary || d.professionalSummary);
    if (Array.isArray(d.workExperience) && d.workExperience.length) {
      setExperiences(
        d.workExperience.map((e, i) => ({
          id: i + 1,
          company: e.company || e.employer || '',
          position: e.position || e.title || e.role || '',
          duration: e.duration || e.dates || '',
          description: Array.isArray(e.description) ? e.description.join('\n') : (e.description || e.responsibilities || ''),
        })),
      );
    }
    if (Array.isArray(d.education) && d.education.length) {
      const valid = d.education.filter((e) => e && (e.institution || e.degree));
      if (valid.length) {
        setEducation(
          valid.map((e, i) => ({
            id: i + 1,
            institution: e.institution || e.school || '',
            degree: e.degree || e.qualification || '',
            field: e.field || e.major || '',
            year: e.year || e.dates || '',
          })),
        );
      }
    }
    if (d.skills) {
      let s = '';
      if (typeof d.skills === 'object' && !Array.isArray(d.skills)) {
        Object.entries(d.skills).forEach(([cat, list]) => {
          if (list && String(list).trim()) s += `• ${cat}: ${list}\n`;
        });
      } else {
        s = Array.isArray(d.skills) ? d.skills.join(', ') : d.skills;
      }
      setSkills(s.trim());
    }
    if (Array.isArray(d.projects) && d.projects.length) {
      setProjects(
        d.projects.map((p, i) => ({
          id: i + 1,
          name: p.name || p.title || '',
          technologies: Array.isArray(p.technologies) ? p.technologies.join(', ') : (p.technologies || p.tech || ''),
          description: Array.isArray(p.description) ? p.description.join('\n') : (p.description || ''),
        })),
      );
    }
    if (d.certifications) {
      let c = '';
      if (Array.isArray(d.certifications)) {
        c = d.certifications
          .map((x) => (typeof x === 'string' ? x : x?.name || ''))
          .filter(Boolean)
          .join(', ');
      } else if (typeof d.certifications === 'string') {
        c = d.certifications;
      }
      setCertifications(c);
    }
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!allowed.includes(file.type)) {
      setParseError('Upload PDF or DOCX only.');
      return;
    }
    if (!requireAuthAndCredits()) return;

    setParsing(true);
    setParseError('');
    setUploadedName(file.name);
    setUsingProfile(false);
    try {
      const ok = await deductCredit();
      if (!ok) { setParsing(false); return; }
      const data = await parseResumeFile(file, jobDescription, keywords);
      if (data) {
        fillFromParsed(data);
        setOpenSection('personal');
        if (fetchCredits) fetchCredits();
      } else {
        setParseError('Could not extract data from the file.');
      }
    } catch (e) {
      setParseError(`Parse failed: ${e.message}`);
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [jobDescription, keywords, fillFromParsed, requireAuthAndCredits, deductCredit, fetchCredits]);

  const onFileInput = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => e.preventDefault();

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some((e) => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach((exp) => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some((e) => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach((edu) => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some((p) => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach((proj) => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  const handleUseProfile = useCallback(async () => {
    if (!profileData) return;
    if (!requireAuthAndCredits()) return;

    setParsing(true);
    setParseError('');
    setUploadedName(`${profileData.fullName || 'Profile'} (from Profile)`);
    setUsingProfile(true);
    try {
      const ok = await deductCredit();
      if (!ok) { setParsing(false); return; }

      const resumeText = buildResumeTextFromProfile(profileData);
      let data;
      if (keywords.length > 0) {
        data = await testResumeParsingWithText(resumeText, jobDescription, keywords);
      } else {
        data = {
          personalInfo: {
            name: profileData.fullName || '',
            email: profileData.email || user?.email || '',
            phone: profileData.phone || '',
            location: profileData.location || '',
            linkedin: profileData.linkedIn || '',
            portfolio: profileData.portfolio || profileData.github || '',
          },
          summary: profileData.professionalSummary || '',
          workExperience: Array.isArray(profileData.workExperience) ? profileData.workExperience : [],
          education: Array.isArray(profileData.education) ? profileData.education : [],
          skills: profileData.skills || '',
          projects: Array.isArray(profileData.projects) ? profileData.projects : [],
          certifications: profileData.certifications || '',
        };
      }
      if (data) {
        fillFromParsed(data);
        setOpenSection('personal');
        if (fetchCredits) fetchCredits();
      }
    } catch (e) {
      setParseError(`Profile load failed: ${e.message}`);
    } finally {
      setParsing(false);
    }
  }, [profileData, requireAuthAndCredits, deductCredit, jobDescription, keywords, fillFromParsed, user, fetchCredits]);

  const prepareData = useCallback(
    () => ({
      personalInfo: {
        name: personalInfo.fullName,
        email: personalInfo.email,
        phone: personalInfo.phone,
        location: personalInfo.location,
        linkedin: personalInfo.linkedIn,
        portfolio: personalInfo.portfolio,
      },
      fullName: personalInfo.fullName,
      name: personalInfo.fullName,
      email: personalInfo.email,
      phone: personalInfo.phone,
      location: personalInfo.location,
      linkedin: personalInfo.linkedIn,
      portfolio: personalInfo.portfolio,
      summary,
      professionalSummary: summary,
      workExperience: experiences,
      workExperiences: experiences,
      education,
      skills,
      projects,
      certifications,
    }),
    [personalInfo, summary, experiences, education, skills, projects, certifications],
  );

  const handleDownload = async () => {
    setDownloadErr('');
    setDownloading(true);
    try {
      const blob = await templateService.generateFromTemplate(selectedTemplate, prepareData());
      const fname = (personalInfo.fullName || 'Resume').replace(/\s+/g, '_');
      saveAs(blob, `${fname}_Resume.docx`);
    } catch (e) {
      setDownloadErr(`Download failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadErr('');
    setDownloadingPdf(true);
    try {
      const blob = await generateResumePdf(selectedTemplate, prepareData());
      const fname = (personalInfo.fullName || 'Resume').replace(/\s+/g, '_');
      saveAs(blob, `${fname}_Resume.pdf`);
    } catch (e) {
      setDownloadErr(`PDF download failed: ${e.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  /* ----------------- ATS score (heuristic) ----------------- */
  const atsScore = useMemo(() => {
    let score = 0;
    if (personalInfo.fullName) score += 8;
    if (personalInfo.email) score += 6;
    if (personalInfo.phone) score += 4;
    if (personalInfo.location) score += 4;
    if (personalInfo.linkedIn) score += 4;
    if (summary && summary.length > 60) score += 14;
    const filledExp = experiences.filter((e) => e.company || e.position).length;
    score += Math.min(20, filledExp * 7);
    const filledEdu = education.filter((e) => e.institution || e.degree).length;
    score += Math.min(10, filledEdu * 5);
    if (skills && skills.length > 30) score += 14;
    const filledProj = projects.filter((p) => p.name).length;
    score += Math.min(8, filledProj * 4);
    if (certifications) score += 4;
    if (keywords.length) {
      const blob = JSON.stringify({ summary, skills, experiences, projects }).toLowerCase();
      const hits = keywords.filter((k) => blob.includes(String(k).toLowerCase())).length;
      score += Math.min(8, Math.round((hits / keywords.length) * 8));
    }
    return Math.min(99, score);
  }, [personalInfo, summary, experiences, education, skills, projects, certifications, keywords]);

  /* ----------------- render ----------------- */
  const sectionDone = (id) => {
    switch (id) {
      case 'personal': return !!(personalInfo.fullName && personalInfo.email);
      case 'summary': return summary.length > 30;
      case 'experience': return experiences.some((e) => e.company && e.position);
      case 'education': return education.some((e) => e.institution && e.degree);
      case 'skills': return skills.length > 10;
      case 'projects': return projects.some((p) => p.name);
      case 'certifications': return certifications.length > 0;
      default: return false;
    }
  };

  const accentForTemplate = TEMPLATE_OPTIONS.find((t) => t.id === selectedTemplate)?.accent || '#e0a3ff';

  // The resume is "created" (downloadable) once there's a name plus at least
  // one filled section — used to enable the top-of-page download button.
  const resumeReady = Boolean(
    (personalInfo.fullName && personalInfo.fullName.trim()) &&
    (summary ||
      skills ||
      experiences.some((e) => e.company || e.position) ||
      education.some((e) => e.institution || e.degree))
  );

  // Show the live preview the moment the user types anything at all
  const hasAnyInput = Boolean(
    Object.values(personalInfo).some((v) => v && v.trim()) ||
    (summary && summary.trim()) ||
    (skills && skills.trim()) ||
    (certifications && certifications.trim()) ||
    experiences.some((e) => e.company || e.position || e.duration || e.description) ||
    education.some((e) => e.institution || e.degree || e.field || e.year) ||
    projects.some((p) => p.name || p.technologies || p.description)
  );

  // Upload / profile source picker stays locked until keywords are extracted
  const sourceLocked = keywords.length === 0;

  return (
    <section className="rbs" id="studio">
      {showInsufficientCredits && (
        <div className="rbs-modal-overlay" onClick={() => setShowInsufficientCredits(false)}>
          <div className="rbs-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Out of credits</h3>
            <p>You need at least 1 credit to optimize your resume.</p>
            <p>Current credits: <strong>{credits}</strong></p>
            <div className="rbs-modal-actions">
              <button
                className="rbl-btn rbl-btn-primary"
                onClick={() => {
                  setShowInsufficientCredits(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}
              >
                Get more credits
              </button>
              <button
                className="rbl-btn rbl-btn-ghost"
                onClick={() => setShowInsufficientCredits(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showKeywords && (
        <div className="rbs-modal-overlay" onClick={() => setShowKeywords(false)}>
          <div className="rbs-modal rbs-kw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rbs-kw-modal-head">
              <h3>Targeting keywords</h3>
              <span className="rbs-kw-modal-count">{keywords.length}</span>
            </div>
            <p className="rbs-kw-modal-sub">Pulled from the job description — weave these into your resume to lift your ATS score.</p>
            <div className="rbs-kw-modal-list">
              {keywords.map((k) => (
                <span key={k} className="rbs-kw">{k}</span>
              ))}
            </div>
            <div className="rbs-modal-actions">
              <button className="rbl-btn rbl-btn-ghost" onClick={() => setShowKeywords(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme switch — parks itself beside the header pill; see
          `.cls-theme-dock` in studioTheme.css. Out of flow in both layouts,
          so `.rbs-workspace`'s viewport-locked height is unaffected. */}

      {/* Two-column workspace */}
      <div className="rbs-workspace">
        {/* LEFT: form */}
        <div className="rbs-left">
          {/* Everything below scrolls. */}
          <div className="rbs-left-scroll">
          {/* Job description bar */}
          <div className="rbs-jdbar">
            <div className="rbs-jdbar-label">
              <span className="rbs-jdbar-title">Job Description</span>
              <span className="rbs-jdbar-hint">Paste the JD to tailor parsing & lift your ATS score</span>
            </div>
            <div className="rbs-jdbar-input">
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste a job description to tailor the AI parsing & ATS score…"
                rows={1}
              />
            </div>
            <div className="rbs-jdbar-actions">
              <button
                type="button"
                className="rbl-btn rbl-btn-primary rbs-jdbar-btn"
                onClick={handleExtractKeywords}
                disabled={extracting}
              >
                {extracting ? 'Extracting…' : 'Extract Keywords'}
              </button>
              <button
                type="button"
                className="rbl-btn rbl-btn-ghost rbs-jdbar-btn"
                onClick={() => setShowKeywords(true)}
                disabled={keywords.length === 0}
                title={keywords.length === 0 ? 'Extract keywords first' : `Show ${keywords.length} extracted keywords`}
              >
                Show Keywords{keywords.length > 0 ? ` (${keywords.length})` : ''}
              </button>
            </div>
          </div>

          {/* Source picker — upload zone + profile shortcut, side-by-side */}
          <div className={`rbs-source ${profileData ? 'has-profile' : ''} ${sourceLocked ? 'is-locked' : ''}`}>
            <div
              className={`rbs-drop ${parsing ? 'busy' : ''} ${sourceLocked ? 'locked' : ''}`}
              onDrop={sourceLocked ? undefined : onDrop}
              onDragOver={sourceLocked ? undefined : onDragOver}
              onClick={() => { if (!sourceLocked) fileRef.current?.click(); }}
              role="button"
              tabIndex={sourceLocked ? -1 : 0}
              aria-disabled={sourceLocked}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                hidden
                disabled={sourceLocked}
                onChange={onFileInput}
              />
              <div className="rbs-drop-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 16V4M12 4L7 9M12 4l5 5M5 20h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="rbs-drop-text">
                <div className="rbs-drop-title">
                  {parsing ? 'Parsing your resume…' : sourceLocked ? 'Extract keywords to unlock' : uploadedName ? `Loaded: ${uploadedName}` : 'Upload PDF or DOCX'}
                </div>
                <div className="rbs-drop-sub">
                  {parsing ? 'Extracting structure & keywords' : sourceLocked ? 'Paste a job description and extract keywords first' : 'Drag & drop or click — AI auto-fills the form'}
                </div>
              </div>
            </div>
            {profileData && (
              <button
                type="button"
                className="rbs-source-alt rbs-source-profile"
                onClick={handleUseProfile}
                disabled={parsing || sourceLocked}
                title={sourceLocked ? 'Extract keywords first' : 'Load your saved profile data'}
              >
                <span>Use my Profile data</span>
                <svg width="14" height="14" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
              </button>
            )}
          </div>

          {parseError && <div className="rbs-error">{parseError}</div>}

          {/* Accordion sections */}
          <div className="rbs-accordion">
            {SECTIONS.map((s) => (
              <div key={s.id} className={`rbs-acc ${openSection === s.id ? 'open' : ''}`}>
                <button
                  type="button"
                  className="rbs-acc-head"
                  onClick={() => setOpenSection(openSection === s.id ? null : s.id)}
                >
                  <span className="rbs-acc-icon">{s.icon}</span>
                  <span className="rbs-acc-title">{s.title}</span>
                  {sectionDone(s.id) && <span className="rbs-acc-check">✓</span>}
                  <span className="rbs-acc-chev">▾</span>
                </button>
                <div className="rbs-acc-body">
                  {s.id === 'personal' && (
                    <div className="rbs-grid-2">
                      <Field label="Full Name" value={personalInfo.fullName} onChange={handlePersonal('fullName')} placeholder="Jane Doe" />
                      <Field label="Email" type="email" value={personalInfo.email} onChange={handlePersonal('email')} placeholder="jane@email.com" />
                      <Field label="Phone" value={personalInfo.phone} onChange={handlePersonal('phone')} placeholder="(555) 555-1234" />
                      <Field label="Location" value={personalInfo.location} onChange={handlePersonal('location')} placeholder="San Francisco, CA" />
                      <Field label="LinkedIn" value={personalInfo.linkedIn} onChange={handlePersonal('linkedIn')} placeholder="linkedin.com/in/janedoe" />
                      <Field label="Portfolio" value={personalInfo.portfolio} onChange={handlePersonal('portfolio')} placeholder="janedoe.dev" />
                    </div>
                  )}

                  {s.id === 'summary' && (
                    <TextArea
                      label="Professional Summary"
                      value={summary}
                      onChange={setSummary}
                      placeholder="3-4 sentences. Lead with role + years + your strongest impact metric."
                      rows={5}
                    />
                  )}

                  {s.id === 'experience' && (
                    <div className="rbs-list">
                      {experiences.map((exp, i) => (
                        <div key={exp.id} className="rbs-item">
                          <div className="rbs-item-head">
                            <span>Experience #{i + 1}</span>
                            <button type="button" className="rbs-rm" onClick={() => removeExp(exp.id)} disabled={experiences.length <= 1}>Remove</button>
                          </div>
                          <div className="rbs-grid-2">
                            <Field label="Company" value={exp.company} onChange={(v) => updateExp(exp.id, 'company', v)} placeholder="Acme Inc." />
                            <Field label="Position" value={exp.position} onChange={(v) => updateExp(exp.id, 'position', v)} placeholder="Senior Engineer" />
                            <Field label="Duration" value={exp.duration} onChange={(v) => updateExp(exp.id, 'duration', v)} placeholder="Jan 2022 – Present" />
                          </div>
                          <TextArea label="What you did (one bullet per line)" value={exp.description} onChange={(v) => updateExp(exp.id, 'description', v)} rows={4} placeholder="• Shipped X, cutting Y by Z%…" />
                        </div>
                      ))}
                      <button type="button" className="rbs-add" onClick={addExp}>+ Add experience</button>
                    </div>
                  )}

                  {s.id === 'education' && (
                    <div className="rbs-list">
                      {education.map((edu, i) => (
                        <div key={edu.id} className="rbs-item">
                          <div className="rbs-item-head">
                            <span>Education #{i + 1}</span>
                            <button type="button" className="rbs-rm" onClick={() => removeEdu(edu.id)} disabled={education.length <= 1}>Remove</button>
                          </div>
                          <div className="rbs-grid-2">
                            <Field label="Institution" value={edu.institution} onChange={(v) => updateEdu(edu.id, 'institution', v)} placeholder="University of …" />
                            <Field label="Degree" value={edu.degree} onChange={(v) => updateEdu(edu.id, 'degree', v)} placeholder="BSc" />
                            <Field label="Field" value={edu.field} onChange={(v) => updateEdu(edu.id, 'field', v)} placeholder="Computer Science" />
                            <Field label="Year" value={edu.year} onChange={(v) => updateEdu(edu.id, 'year', v)} placeholder="2018 – 2022" />
                          </div>
                        </div>
                      ))}
                      <button type="button" className="rbs-add" onClick={addEdu}>+ Add education</button>
                    </div>
                  )}

                  {s.id === 'skills' && (
                    <TextArea
                      label="Skills (comma-separated or grouped per line)"
                      value={skills}
                      onChange={setSkills}
                      rows={5}
                      placeholder="React, TypeScript, Node.js, AWS, PostgreSQL, Docker, CI/CD…"
                    />
                  )}

                  {s.id === 'projects' && (
                    <div className="rbs-list">
                      {projects.map((p, i) => (
                        <div key={p.id} className="rbs-item">
                          <div className="rbs-item-head">
                            <span>Project #{i + 1}</span>
                            <button type="button" className="rbs-rm" onClick={() => removeProj(p.id)} disabled={projects.length <= 1}>Remove</button>
                          </div>
                          <div className="rbs-grid-2">
                            <Field label="Name" value={p.name} onChange={(v) => updateProj(p.id, 'name', v)} placeholder="Project Name" />
                            <Field label="Technologies" value={p.technologies} onChange={(v) => updateProj(p.id, 'technologies', v)} placeholder="Next.js, OpenAI" />
                          </div>
                          <TextArea label="Description" value={p.description} onChange={(v) => updateProj(p.id, 'description', v)} rows={3} placeholder="What you built and the impact" />
                        </div>
                      ))}
                      <button type="button" className="rbs-add" onClick={addProj}>+ Add project</button>
                    </div>
                  )}

                  {s.id === 'certifications' && (
                    <TextArea
                      label="Certifications"
                      value={certifications}
                      onChange={setCertifications}
                      rows={3}
                      placeholder="AWS Solutions Architect, …"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* RIGHT: live preview + controls */}
        <div className="rbs-right">
          <div className="rbs-rightcard" style={{ '--accent': accentForTemplate }}>
            <div className="rbs-tpl-row">
              <span className="rbs-tpl-label">Template</span>
              <div className="rbs-tpl-pills">
                {TEMPLATE_OPTIONS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`rbs-tpl ${selectedTemplate === t.id ? 'active' : ''}`}
                    style={{ '--accent': t.accent }}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              <div className="rbs-score rbs-score--inline">
                <div className="rbs-score-label">ATS readiness</div>
                <div className="rbs-score-val">{atsScore}<span>/100</span></div>
              </div>
            </div>

            {/* Resume preview paper (zoomable + pannable) */}
            <div className="rbs-preview">
              <div
                ref={viewportRef}
                className={`rbs-preview-viewport ${!hasAnyInput ? 'is-empty' : ''} ${effectiveScale > BASE_SCALE ? 'is-zoomed' : ''} ${panning ? 'is-panning' : ''}`}
                onMouseDown={onPanStart}
                onMouseMove={onPanMove}
                onMouseUp={onPanEnd}
                onMouseLeave={onPanEnd}
              >
                {!hasAnyInput ? (
                  <div className="rbs-paper rbs-paper--empty">
                    <div className="rbs-paper-empty">
                      <span className="rbs-paper-empty-icon">📄</span>
                      <p className="rbs-paper-empty-title">Fill the sections on the left to preview your resume</p>
                      <p className="rbs-paper-empty-sub">Start with your name and details — your resume builds here live.</p>
                    </div>
                  </div>
                ) : (
                <>
                <div className="rbs-zoom-controls" onMouseDown={(e) => e.stopPropagation()}>
                  <button type="button" className="rbs-zoom-btn" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title="Zoom out ( − )" aria-label="Zoom out">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                  <button type="button" className="rbs-zoom-level" onClick={zoomReset} title="Reset zoom ( 0 )">{Math.round(zoom * 100)}%</button>
                  <button type="button" className="rbs-zoom-btn" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title="Zoom in ( + )" aria-label="Zoom in">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                </div>
                <div
                  ref={stageRef}
                  className="rbs-preview-stage"
                  style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${effectiveScale})` }}
                >
            <div className={`rbs-paper rbs-paper--${(TEMPLATE_OPTIONS.find((t) => t.id === selectedTemplate)?.name || 'Professional').toLowerCase()}`}>
              <div className="rbs-paper-head">
                <h3>{personalInfo.fullName || 'Your Name'}</h3>
                <div className="rbs-paper-contact">
                  {[personalInfo.location, personalInfo.phone, personalInfo.email, personalInfo.linkedIn, personalInfo.portfolio]
                    .filter(Boolean)
                    .join('  |  ') || 'location | phone | email | linkedin | portfolio'}
                </div>
              </div>

              {education.some((e) => e.institution || e.degree) && (
                <div className="rbs-paper-section rbs-paper-section--education">
                  <h4>Education</h4>
                  {education.filter((e) => e.institution || e.degree).map((e) => (
                    <div key={e.id} className="rbs-paper-edu">
                      <strong>{e.degree}{e.field ? `, ${e.field}` : ''}{e.institution ? `, ${e.institution}` : ''}</strong>
                      {e.year ? <span>{`  (${e.year})`}</span> : ''}
                    </div>
                  ))}
                </div>
              )}

              {summary && (
                <div className="rbs-paper-section rbs-paper-section--summary">
                  <h4>{selectedTemplate === 4 ? 'Professional Summary' : 'Summary'}</h4>
                  <p>{summary}</p>
                </div>
              )}

              {skills && (
                <div className="rbs-paper-section rbs-paper-section--skills">
                  <h4>{selectedTemplate === 3 ? 'Technical Skills' : 'Skills'}</h4>
                  <ul className="rbs-paper-skills">
                    {skills.split('\n').map((s) => s.replace(/^[•\-*]\s?/, '').trim()).filter(Boolean).map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}

              {experiences.some((e) => e.company || e.position) && (
                <div className="rbs-paper-section rbs-paper-section--experience">
                  <h4>Experience</h4>
                  {experiences.filter((e) => e.company || e.position).map((e) => (
                    <div key={e.id} className="rbs-paper-job">
                      <div className="rbs-paper-job-head">
                        <span className="rbs-paper-job-title">
                          <strong>{e.company || 'Company'}</strong>
                          {e.position ? <em className="rbs-paper-job-pos">{e.position}</em> : null}
                        </span>
                        <span>{e.duration}</span>
                      </div>
                      {e.description && (
                        <ul>
                          {e.description.split('\n').filter(Boolean).slice(0, 6).map((line, i) => (
                            <li key={i}>{line.replace(/^[•\-*]\s?/, '')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {projects.some((p) => p.name) && (
                <div className="rbs-paper-section rbs-paper-section--projects">
                  <h4>Projects</h4>
                  {projects.filter((p) => p.name).map((p) => (
                    <div key={p.id} className="rbs-paper-proj">
                      <div className="rbs-paper-proj-head">
                        <strong>{p.name}</strong>
                        {p.technologies ? <strong className="rbs-paper-proj-tech-inline">{` | ${p.technologies}`}</strong> : null}
                      </div>
                      {p.technologies ? <div className="rbs-paper-proj-tech-line">Technologies: {p.technologies}</div> : null}
                      {p.description && (
                        <ul className="rbs-paper-proj-desc">
                          {p.description.split('\n').map((line) => line.replace(/^[•\-*]\s?/, '').trim()).filter(Boolean).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {certifications && (
                <div className="rbs-paper-section rbs-paper-section--certifications">
                  <h4>Certifications</h4>
                  <p>{certifications}</p>
                </div>
              )}
            </div>
                </div>
                </>
                )}
              </div>
            </div>

            <div className="rbs-actions">
              <div className="rbs-download-row">
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary rbs-download"
                  onClick={handleDownload}
                  disabled={downloading || !resumeReady}
                  title={resumeReady ? 'Download your resume (.docx)' : 'Add your details first to enable download'}
                >
                  {downloading ? 'Building DOCX…' : 'Download .docx'}
                  {!downloading && (
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 4v12M6 10l6 6 6-6M5 20h14"/></svg>
                  )}
                </button>
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary rbs-download rbs-download--pdf"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || !resumeReady}
                  title={resumeReady ? 'Download your resume (.pdf)' : 'Add your details first to enable download'}
                >
                  {downloadingPdf ? 'Building PDF…' : 'Download PDF'}
                  {!downloadingPdf && (
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 4v12M6 10l6 6 6-6M5 20h14"/></svg>
                  )}
                </button>
              </div>
              {downloadErr && <div className="rbs-error">{downloadErr}</div>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResumeBuilderStudio;
